import type {
  AddStakeholderRequestPayload,
  StakeholderType,
  AiExtractDataResponse,
  ExtractionTarget,
  Stakeholder,
  AiExtractDataRequestPayload
} from '@gat-ship/types'
import { appLoadingKey } from '@gat-ship/types/injection-keys'
import { getCurrentInstance, inject, nextTick, ref, type Ref } from 'vue'
import { createContext } from 'reka-ui'
import { globalUtils } from '@gat-ship/utils'
import { useAiApiCalls } from '@gat-ship/smart-paste'
import { useSelectCountryDataSource } from '@gat-ship/ref-data'
import { AppError, ErrorCode } from '@gat-ship/error-handling'
import { useStakeholderApiCalls } from './useStakeholderApiCalls'
import {
  buildInitialCreatePayload,
  mapRequestPayloadToValidRequestPayload
} from '../model/create-stakeholder-payload-builder'
import { getCreateSmartPasteTarget } from '@gat-ship/stakeholders'

export interface ModalStakeholderCreateParams {
  stakeholderType: StakeholderType
  requestPayload?: AddStakeholderRequestPayload
  /** Invoked once with the created stakeholder on a successful save. */
  onCreated?: (stakeholder: Stakeholder) => void
}

export type CreateStakeholderProviderEmits = {
  (e: 'stakeholder-created', stakeholder: Stakeholder): void
}

/**
 * The small, purpose-built surface the create-stakeholder form exposes to
 * its provider. The provider only ever needs to validate the form and push
 * AI-extracted values into it.
 */
export interface CreateStakeholderFormApi {
  /** Run submit-time validation (zod schema + dynamic custom fields). */
  validate(): Promise<boolean>
  /** Apply an AI-extraction response into the form's fields. */
  applyAiValues(values: Record<string, unknown>): void
}

export interface CreateStakeholderModal {
  showModal(params: ModalStakeholderCreateParams): void
  closeModal(): void
}

export type CreateStakeholderModals = {
  stakeholderCreate: Ref<CreateStakeholderModal | null>
}

/** Self-inject the app-loading registry (UI-free). */
function useLoading() {
  return getCurrentInstance() ? inject(appLoadingKey, undefined) : undefined
}

function defineCreateStakeholderState() {
  const formApi = ref<CreateStakeholderFormApi | null>(null)

  const requestPayload = ref<AddStakeholderRequestPayload>()

  function setRequestPayload(
    payload: AddStakeholderRequestPayload | undefined
  ) {
    requestPayload.value = payload
  }

  /** One-shot callback fired with the created stakeholder on a successful save. */
  const onCreated = ref<((stakeholder: Stakeholder) => void) | null>(null)
  function setOnCreated(cb: ((stakeholder: Stakeholder) => void) | null) {
    onCreated.value = cb
  }

  function initializeRequestPayload(params: {
    stakeholderType: StakeholderType
    formApi: CreateStakeholderFormApi
  }) {
    const { stakeholderType } = params
    if (!params.formApi) {
      console.error('No form API found in create-stakeholder-provider.vue')
    }
    formApi.value = params.formApi
    setRequestPayload(buildInitialCreatePayload(stakeholderType))
  }

  const countryDataSource = useSelectCountryDataSource()

  function getSmartPasteTarget(
    stakeholderType: StakeholderType
  ): ExtractionTarget {
    return getCreateSmartPasteTarget(stakeholderType)
  }

  function setAiValues(payload: AiExtractDataResponse) {
    if (!requestPayload.value) {
      return
    }
    formApi.value?.applyAiValues(payload)
    countryDataSource.fetchCountries()
    if ('mainContacts' in payload && payload.mainContacts) {
      requestPayload.value.mainContacts = payload.mainContacts.map(
        (contact) => ({
          id: globalUtils.functions.uuid(),
          firstName: contact.firstName.value ?? '',
          lastName: contact.lastName.value ?? '',
          jobTitle: contact.jobTitle.value,
          directLine: contact.directLine.value,
          mobilePhone: contact.mobilePhone.value,
          email: contact.email.value,
          department: contact.department.value
        })
      )
    }
  }

  return {
    requestPayload,
    setRequestPayload,
    initializeRequestPayload,
    setAiValues,
    formApi,
    getSmartPasteTarget,
    onCreated,
    setOnCreated
  }
}

function defineCreateStakeholderCommand(
  state: ReturnType<typeof defineCreateStakeholderState>,
  modalRefs: CreateStakeholderModals,
  emit: CreateStakeholderProviderEmits
) {
  const { addStakeholderRequest } = useStakeholderApiCalls()
  const { extractDataRequest } = useAiApiCalls()

  const loading = useLoading()

  async function extractAiData(
    payload: AiExtractDataRequestPayload,
    stakeholderType: StakeholderType
  ) {
    const loadingIds = ['smart-paste-extract']
    const target = state.getSmartPasteTarget(stakeholderType)
    try {
      loading?.setLoadingId(loadingIds)
      const response = await extractDataRequest({
        ...payload,
        target
      })
      if (!response || !response.data) {
        throw new AppError({
          message: 'No data extracted',
          code: ErrorCode.UNEXPECTED,
          details: {}
        })
      }
      if (response.data) {
        state.setAiValues(response.data)
      }
    } finally {
      loading?.removeLoadingId(loadingIds)
    }
  }

  async function extractAiDataAndShowModal(
    text: string,
    stakeholderType: StakeholderType
  ) {
    const loadingIds = ['smart-paste-extract']
    const target = state.getSmartPasteTarget(stakeholderType)
    try {
      loading?.setLoadingId(loadingIds)
      const response = await extractDataRequest({
        target,
        text,
        customPromptAssetId: null,
        aiModelOverride: null
      })
      if (!response || !response.data) {
        throw new AppError({
          message: 'No data extracted',
          code: ErrorCode.UNEXPECTED,
          details: {}
        })
      }
      if (response.data) {
        modalRefs.stakeholderCreate.value?.showModal({
          stakeholderType: stakeholderType,
          requestPayload: state.requestPayload.value
        })
        nextTick(() => {
          state.setAiValues(response.data)
        })
      }
    } finally {
      loading?.removeLoadingId(loadingIds)
    }
  }

  async function createStakeholder() {
    const formApi = state.formApi.value
    if (!formApi) {
      throw new AppError({
        message: 'Form reference not found',
        code: ErrorCode.UNEXPECTED,
        details: {}
      })
    }
    const valid = await formApi.validate()
    if (!valid) {
      return {
        error: 'Please fill in all required fields',
        requestPayload: state.requestPayload.value
      }
    }
    const payload = mapRequestPayloadToValidRequestPayload(
      state.requestPayload.value
    )
    if (!payload) {
      return {
        error: 'Please fill in all required fields',
        requestPayload: state.requestPayload.value
      }
    }
    const loadingIds = [payload.id, 'loading-stakeholder-create']
    loading?.setLoadingId(loadingIds)
    modalRefs.stakeholderCreate.value?.closeModal()
    try {
      const result = await addStakeholderRequest({
        payload,
        commandId: globalUtils.functions.uuid()
      })
      if (result && 'newState' in result) {
        emit('stakeholder-created', result.newState)
        state.onCreated.value?.(result.newState)
        state.setOnCreated(null)
      }
      return result
    } catch {
      modalRefs.stakeholderCreate.value?.showModal({
        stakeholderType: payload.stakeholderType,
        requestPayload: state.requestPayload.value
      })
      return undefined
    } finally {
      loading?.removeLoadingId(loadingIds)
    }
  }

  return {
    extractAiData,
    extractAiDataAndShowModal,
    createStakeholder
  }
}

export function defineCreateStakeholderContext(
  modalRefs: CreateStakeholderModals,
  emit: CreateStakeholderProviderEmits
) {
  const state = defineCreateStakeholderState()
  const commands = defineCreateStakeholderCommand(state, modalRefs, emit)
  return {
    requestPayload: state.requestPayload,
    initializeRequestPayload: state.initializeRequestPayload,
    getSmartPasteTarget: state.getSmartPasteTarget,
    setAiValues: state.setAiValues,
    commands,
    modals: {
      showModalStakeholderCreate: (params: ModalStakeholderCreateParams) => {
        state.setOnCreated(params.onCreated ?? null)
        modalRefs.stakeholderCreate.value?.showModal(params)
      }
    }
  }
}

export type CreateStakeholderContext = ReturnType<
  typeof defineCreateStakeholderContext
>

const [_useCreateStakeholder, provideCreateStakeholder] =
  createContext<CreateStakeholderContext>('gs-create-stakeholder-provider')

export { provideCreateStakeholder }

export function useCreateStakeholder() {
  return _useCreateStakeholder() as CreateStakeholderContext
}
